/**
 * Audio Manager for point balance change sound effects
 * Handles preloading, caching, and playback of coin sounds
 */

const AUDIO_BASE_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/audio';

type SoundType = 'coins-big' | 'coins-medium' | 'coins-small';

// Audio cache for preloaded sounds
const audioCache: Map<SoundType, HTMLAudioElement> = new Map();

// Track if audio has been initialized
let isInitialized = false;

/**
 * Preload a specific audio file
 */
const preloadAudio = (type: SoundType): HTMLAudioElement => {
  if (audioCache.has(type)) {
    return audioCache.get(type)!;
  }

  const audio = new Audio(`${AUDIO_BASE_URL}/${type}.wav`);
  audio.preload = 'auto';
  audioCache.set(type, audio);
  return audio;
};

/**
 * Initialize audio manager by preloading all sounds
 * Call this once on app initialization
 */
export const initAudioManager = (): void => {
  if (isInitialized) return;

  try {
    preloadAudio('coins-big');
    preloadAudio('coins-medium');
    preloadAudio('coins-small');
    isInitialized = true;
    console.log('[AudioManager] Initialized and preloaded sounds');
  } catch (error) {
    console.warn('[AudioManager] Failed to preload audio:', error);
  }
};

/**
 * Determine which sound to play based on point change magnitude
 */
const getSoundType = (change: number): SoundType => {
  const absChange = Math.abs(change);

  if (absChange >= 1000) {
    return 'coins-big';
  } else if (absChange >= 100) {
    return 'coins-medium';
  } else {
    return 'coins-small';
  }
};

/**
 * Play the appropriate coin sound based on point change amount
 * @param change - The point change amount (positive or negative)
 * @param enabled - Whether sound effects are enabled (from user settings)
 */
export const playPointsSound = (change: number, enabled: boolean = true): void => {
  if (!enabled) {
    return;
  }

  const soundType = getSoundType(change);

  try {
    // Get the preloaded audio or load it now
    const sourceAudio = audioCache.get(soundType) || preloadAudio(soundType);

    // Clone the audio element to allow overlapping sounds
    const playInstance = sourceAudio.cloneNode() as HTMLAudioElement;
    playInstance.volume = 0.5;

    // Attempt to play - may be blocked by browser autoplay policy
    playInstance.play().catch((err) => {
      // Browser may block autoplay before user interaction - silently fail
      console.warn('[AudioManager] Audio playback blocked:', err.message);
    });
  } catch (error) {
    console.warn('[AudioManager] Failed to play sound:', error);
  }
};

/**
 * Check if audio manager is initialized
 */
export const isAudioInitialized = (): boolean => isInitialized;

/**
 * Get the volume level for a sound type (for debugging/testing)
 */
export const getSoundVolume = (type: SoundType): number => {
  const audio = audioCache.get(type);
  return audio?.volume ?? 0;
};
